import { useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { CheckCircle2 } from "lucide-react";

import { useResultQuery } from "src/lib/endpoints/question/question.api";
import { examRoleQuery, getExamRole } from "src/lib/auth/examRole";
import { DashboardLink, Eyebrow, Meter, PrimaryButton } from "./ui";

/**
 * Shown once the test is submitted, in place of the shared `TestScoreModal`.
 *
 * It cannot be dismissed. Behind it is a test that has already been handed
 * in, so the only useful ways on are the full result or the dashboard; the
 * old modal closed on an outside click and left the talent on a dead exam.
 *
 * A score of 0 is a score. The old modal tested `total_score` for truthiness
 * and showed nothing at all for it.
 */
const ScoreDialog = ({ resultsPath }: { resultsPath: string }) => {
  const navigate = useNavigate();
  const { result, isLoading, error } = useResultQuery();
  const dialogRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    dialogRef.current?.focus();
  }, []);

  const score =
    result?.total_score === null || result?.total_score === undefined
      ? null
      : Number(result.total_score);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-brand-navy/40 p-5 backdrop-blur-[2px]">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="score-dialog-title"
        tabIndex={-1}
        className="w-full max-w-sm rounded-2xl border border-border bg-card p-6 text-center font-sans text-brand-navy shadow-ui-lg outline-none sm:p-8"
      >
        <span className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-brand-orange-100 text-brand-orange">
          <CheckCircle2 className="h-6 w-6" aria-hidden="true" />
        </span>

        <div className="mt-4">
          <Eyebrow>
            {getExamRole() === "secondary"
              ? "Secondary-role test submitted"
              : "Test submitted"}
          </Eyebrow>
        </div>
        <h2
          id="score-dialog-title"
          className="mt-1 font-display text-2xl font-extrabold tracking-tight"
        >
          Your score
        </h2>

        <div className="my-6">
          {score !== null ? (
            <>
              <div className="font-display text-6xl font-extrabold tabular-nums">
                {score}
                <span className="text-3xl">%</span>
              </div>
              <Meter value={score} tone="orange" className="mt-4" label="Total score" />
            </>
          ) : (
            <p className="text-sm leading-relaxed text-muted-foreground">
              {isLoading
                ? "Calculating your score…"
                : error
                ? "Your answers are in. Your score will be on the full result page."
                : "Your score isn't available yet. Check the full result page."}
            </p>
          )}
        </div>

        <PrimaryButton
          className="w-full"
          onClick={() => navigate(`${resultsPath}${examRoleQuery()}`)}
        >
          See full result
        </PrimaryButton>
        <DashboardLink className="mt-4 inline-block" />
      </div>
    </div>
  );
};

export default ScoreDialog;
